import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { useUnsavedChangesGuard } from "@/components/ui/entity-form";

/**
 * Warn before an editor with unsaved work is left, by either exit.
 *
 * `useUnsavedChangesGuard` handles `beforeunload` -- closing the tab -- and hands
 * back a `confirmLeave` the caller wraps its own navigations in. That covers the
 * exits an editor INITIATES, and not the one an operator actually takes: the
 * links they leave by are `NavLink`s in `AdminShell`, a parent layout route the
 * editor does not render and therefore cannot wrap.
 *
 * `useBlocker` is what covers those. It needs a data router, which this app has
 * (`createBrowserRouter` in `routes/index.tsx`), and it intercepts a navigation
 * before the editor unmounts rather than after.
 *
 * ONE HOOK RATHER THAN THREE COPIES. The product, brand and spec-template
 * editors differ in what "dirty" means and agree exactly on what to do about it,
 * which is the same split `useUnsavedChangesGuard` documents for itself -- so
 * `isDirty` stays the caller's to compute and the exits live here.
 */
export interface EditorExitGuard {
	/**
	 * Let the NEXT navigation through without prompting.
	 *
	 * For an editor's own post-save redirect. A successful create navigates to
	 * the row it just created, and the draft is still populated at that moment --
	 * so the blocker asked "You have unsaved changes. Leave without saving?"
	 * about work the server had just accepted. An operator who reads that and
	 * clicks Cancel is stranded on the create screen with the record already
	 * made; pressing Save again is refused as a duplicate.
	 *
	 * A REF RATHER THAN CLEARING THE DRAFT FIRST, because clearing it does not
	 * work: React batches the state update, so the navigation is dispatched
	 * before the re-render that would make `isDirty` false, and `useBlocker`
	 * still sees the previous render's value. The ref is read at navigation
	 * time, which is the only moment that helps.
	 */
	allowNextNavigation: () => void;
}

export function useEditorExitGuard(isDirty: boolean): EditorExitGuard {
	// The tab-close half, and the `confirmLeave` this file does not need: the
	// blocker below covers in-app navigation without the caller wrapping anything.
	useUnsavedChangesGuard({ isDirty });

	const bypass = useRef(false);

	/*
	 * Every navigation while dirty, with no path comparison.
	 *
	 * A pathname comparison would guard nothing here: there is no same-path
	 * navigation in this app. The product editor's open tab looks like a
	 * candidate and is not -- `tab` is local state and `setTab` touches no
	 * router, so switching tabs is not a navigation and never reaches a blocker.
	 */
	const blocker = useBlocker(() => isDirty && !bypass.current);

	useEffect(() => {
		if (blocker.state !== "blocked") return;

		/*
		 * `window.confirm`, matching `useUnsavedChangesGuard`'s own prompt rather
		 * than inventing a second one. A custom dialog is better and is a
		 * different change: it would have to live above the editors, since this
		 * fires while a navigation is suspended.
		 */
		if (window.confirm("You have unsaved changes. Leave without saving?")) {
			blocker.proceed();
		} else {
			/*
			 * RESET, not just "do nothing". A blocker left in `blocked` state
			 * refuses every subsequent navigation silently -- the operator clicks
			 * Cancel once and the sidebar stops working, with nothing on screen to
			 * explain it.
			 */
			blocker.reset();
		}
	}, [blocker]);

	// Armed for one navigation only. Left set, the editor would stop guarding
	// altogether after its first save.
	useEffect(() => {
		if (blocker.state === "unblocked") bypass.current = false;
	}, [blocker.state]);

	return {
		allowNextNavigation: useCallback(() => {
			bypass.current = true;
		}, []),
	};
}
